import {Component, Inject} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialog, MatDialogRef} from '@angular/material/dialog';
import {Router} from '@angular/router';
import {ExtensionStatusService, ExtensionStatus} from '@app/services/extension-status.service';
import {
  dismissQuickEntryDialogForever,
  snoozeQuickEntryDialogForOneDay,
} from '@app/util/quick-entry-dismissal.helper';
import {TranslationService} from '@app/services/translation.service';
import {NotificationService} from '@app/services/notification.service';

export interface ExtensionStatusDialogData {
  status: Extract<ExtensionStatus, 'not-installed' | 'installed-logged-out'>;
  userId: string;
  /** Whether closing/dismissing this dialog should snooze or permanently
   *  suppress the app-wide QuickEntry nag (app.component.ts). Defaults to
   *  true for that nag's own use — set false when opening this dialog from
   *  a different, narrower context (e.g. a per-vehicle row icon, BRIEFD-7331)
   *  so a casual close there doesn't silently kill the global reminder. */
  suppressesGlobalNag?: boolean;
}

@Component({
  selector: 'app-extension-status-dialog',
  templateUrl: './extension-status-dialog.component.html',
  styleUrls: ['./extension-status-dialog.component.scss'],
  standalone: false
})
export class ExtensionStatusDialogComponent {
  public status: ExtensionStatusDialogData['status'];
  public isLoggingIn = false;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: ExtensionStatusDialogData,
    public dialogRef: MatDialogRef<ExtensionStatusDialogComponent>,
    private router: Router,
    private extensionStatusService: ExtensionStatusService,
    private notificationService: NotificationService,
    public translate: TranslationService,
  ) {
    this.status = data.status;
  }

  static open(dialog: MatDialog, data: ExtensionStatusDialogData) {
    return dialog.open(ExtensionStatusDialogComponent, {
      width: '500px',
      data,
    });
  }

  goToConfigPage(): void {
    this.router.navigate(['/admin/settings/advertising']);
    this.dialogRef.close();
  }

  login(): void {
    this.isLoggingIn = true;
    this.extensionStatusService.login()
      .then((success) => {
        this.isLoggingIn = false;
        if (success) {
          this.notificationService.showSuccessAlert(this.translate.getTranslation('successfulExtensionLogin'));
          this.dialogRef.close();
        } else {
          this.notificationService.showErrorAlert('Error logging in to extension');
        }
      })
      .catch(() => {
        this.isLoggingIn = false;
        this.notificationService.showErrorAlert('Error logging in to extension');
      });
  }

  get suppressesGlobalNag(): boolean {
    return this.data.suppressesGlobalNag ?? true;
  }

  close(): void {
    if (this.suppressesGlobalNag) {
      snoozeQuickEntryDialogForOneDay(this.data.userId);
    }
    this.dialogRef.close();
  }

  dismissForever(): void {
    if (this.suppressesGlobalNag) {
      dismissQuickEntryDialogForever(this.data.userId);
    }
    this.dialogRef.close();
  }
}
